import { useState } from 'react';
import { Button, Input, PasswordInput } from '@tutero/ui';

import { requestEmailChange, type Account } from '@/lib/account';

import { Field, SettingsSection } from './SettingsSection';
import { reasonFor, useAccountWrite } from './useAccountWrite';

/**
 * The sign-in address (`settings-is-a-dialog` D4, D5). The Worker's own gate takes the
 * current password, then mails the NEW address; the address moves when that link is
 * followed, so this says a link was sent and never that the address changed. Flutter said
 * the same sentence and then wrote the unverified address into its database on the next
 * line (`contact_details_section_bloc.dart:88-102`).
 *
 * An address already on another account answers exactly as a free one and gets no mail:
 * the Worker never says which addresses exist, and this does not guess.
 */
export function AccountEmailSection({ account }: { account: Account }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const ask = useAccountWrite(requestEmailChange);

  const next = email.trim().toLowerCase();
  const moved = next !== '' && next !== account.email.toLowerCase();
  const ready = moved && password !== '';

  return (
    <SettingsSection
      title="Your email address"
      summary={`You sign in as ${account.email}.`}
      error={ask.isError ? reasonFor(ask.error) : null}
      done={
        sentTo
          ? `We sent a link to ${sentTo}. Your sign-in address changes when you follow it.`
          : null
      }
      onSubmit={() => {
        if (!ready || ask.isPending) return;
        setSentTo(null);
        ask.mutate(
          { newEmail: next, password },
          {
            onSuccess: () => {
              setSentTo(next);
              /* The password is spent; the address stays, so the sentence names something on screen. */
              setPassword('');
            },
          },
        );
      }}
      footer={
        <Button type="submit" disabled={!ready || ask.isPending}>
          {ask.isPending ? 'Sending…' : 'Send Verification'}
        </Button>
      }
    >
      <Field id="settings-email" label="New email address">
        <Input
          id="settings-email"
          type="email"
          value={email}
          autoComplete="email"
          onChange={(event) => {
            setEmail(event.target.value);
            setSentTo(null);
          }}
        />
      </Field>
      <Field
        id="settings-email-password"
        label="Your current password"
        about="Tutero will not move a sign-in address without it."
      >
        <PasswordInput
          id="settings-email-password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
    </SettingsSection>
  );
}
